/* global gdAlertify */
import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatMoney } from "../utils/utils";
import CartolaPacienteModal from "./components/CartolaPacienteModal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FIRMA_DEFAULT } from "../utils/firmaCorreo";
import MonthInput from "./components/MonthInput";

const monthNames = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const formatearMes = (periodoStr) => {
    if (!periodoStr) return "";
    const [year, month] = periodoStr.split("-");
    const nombreMes = monthNames[parseInt(month, 10)] || "";
    return `${nombreMes.charAt(0).toUpperCase()}${nombreMes.slice(1)} ${year}`;
};

const formatearMesCorreo = (periodoStr) => {
    if (!periodoStr) return "";
    const [year, month] = periodoStr.split("-");
    const nombreMes = monthNames[parseInt(month, 10)] || "";
    return `${nombreMes} ${year}`.trim();
};

const formatearListaNumerosFactura = (facturas = []) => {
    const numeros = facturas.map(factura => factura.numero_factura || "......");
    if (numeros.length <= 1) return numeros[0] || "......";
    if (numeros.length === 2) return `${numeros[0]} y ${numeros[1]}`;
    return `${numeros.slice(0, -1).join(", ")} y ${numeros[numeros.length - 1]}`;
};

const crearMensajeFacturasDefault = (periodo, facturas = []) => {
    const mesPeriodo = formatearMesCorreo(periodo);
    const cantidad = facturas.length || 1;
    const numeros = formatearListaNumerosFactura(facturas);

    let mensaje;
    if (cantidad === 1) {
        mensaje = (
            `<p>Estimado(a), adjunto envío Factura Exenta n° ${numeros}, correspondiente <strong>a lo ya cancelado por Ud</strong> por servicios prestados por nuestra empresa durante el mes de ${mesPeriodo}.</p>` +
            `<p>Esta factura, más el detalle del mes que le enviamos con la cobranza, le podrían servir para obtener el reembolso de la Isapre del paciente, si su plan así lo considera.</p>` +
            `<p>Gracias y saludos,</p>`
        );
    } else {
        mensaje = (
            `<p>Estimado(a), adjunto envío Facturas Exentas n° ${numeros}, correspondientes <strong>a lo ya cancelado por Ud</strong> por servicios prestados por nuestra empresa durante el mes de ${mesPeriodo}.</p>` +
            `<p>Estas facturas, más el detalle del mes correspondiente que le enviamos con la cobranza, le podrían servir para obtener el reembolso de la Isapre del paciente, si su plan así lo considera.</p>` +
            `<p>Gracias y saludos,</p>`
        );
    }

    return mensaje;
};

// Email de notificación: tutor de pago -> tutor de salud -> primer correo de
// "Correos de notificación" del paciente. Un solo email (ya resuelto en el
// backend, ver AcuerdoMutuoController::calcularCobranzaFacturasPeriodo).
const emailNotificacionPaciente = (row = {}) => row.email_notificacion || "";

const CobranzaFacturas = ({ fecha, isPage = false }) => {
    const [resultados, setResultados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verHistorial, setVerHistorial] = useState(null); // { id, nombre, facturas }
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [showCartolaModal, setShowCartolaModal] = useState(false);
    const [selectedPacienteCartola, setSelectedPacienteCartola] = useState(null);

    const [showAddFacturaModal, setShowAddFacturaModal] = useState(false);
    const [addFacturaPaciente, setAddFacturaPaciente] = useState(null);
    const [existingFacturaRows, setExistingFacturaRows] = useState([]);
    const [loadingExistingFacturas, setLoadingExistingFacturas] = useState(false);
    const [addFacturaRows, setAddFacturaRows] = useState([]);
    const [savingFacturas, setSavingFacturas] = useState(false);

    // Estados para notificación de facturas
    const [showNotificarModal, setShowNotificarModal] = useState(false);
    const [notificarRow, setNotificarRow] = useState(null);
    const [emailDestinatarios, setEmailDestinatarios] = useState("");
    const [emailAsunto, setEmailAsunto] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [sendingNotificacion, setSendingNotificacion] = useState(false);
    const [notificarFacturas, setNotificarFacturas] = useState([]);
    const [loadingNotificarFacturas, setLoadingNotificarFacturas] = useState(false);
    const [pdfLigeroBase64, setPdfLigeroBase64] = useState(null);
    const [adjuntarPdfLigero, setAdjuntarPdfLigero] = useState(false);
    const [adjuntoExtra, setAdjuntoExtra] = useState(null);
    const [adjuntoExtraBase64, setAdjuntoExtraBase64] = useState(null);

    const [fechaFilter, setFechaFilter] = useState("");

    const buscarFacturas = async () => {
        const queryFecha = isPage ? fechaFilter : fecha;
        if (!queryFecha) return;
        setLoading(true);
        try {
            const params = { fecha: queryFecha };
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza/facturas", { params });
            setResultados(response.data.data || []);
        } catch (error) {
            console.error("Error al buscar facturas:", error);
            gdAlertify.notiError("Error al cargar la vista de facturas");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!isPage && fecha) {
            buscarFacturas();
        } else if (!isPage) {
            setResultados([]);
        }
    }, [fecha, isPage]);

    const loadExistingFacturasForModal = async (row) => {
        const queryFecha = isPage ? fechaFilter : fecha;
        if (!row?.paciente_id || !queryFecha) {
            setExistingFacturaRows([]);
            return;
        }

        setLoadingExistingFacturas(true);
        try {
            const resp = await axiosInstance.get(`/acuerdo-mutuo/cobranza/facturas/historial/${row.paciente_id}`);
            if (resp.data && !resp.data.error) {
                const facturasPeriodo = (resp.data.data || [])
                    .filter(factura => factura.periodo === queryFecha && factura.archivo_path)
                    .map(factura => ({
                        ...factura,
                        saving: false,
                        deleting: false
                    }));
                setExistingFacturaRows(facturasPeriodo);
            } else {
                setExistingFacturaRows([]);
                gdAlertify.notiError(resp.data.message || 'Error al cargar facturas del periodo.');
            }
        } catch (error) {
            console.error('Error al cargar facturas del periodo:', error);
            setExistingFacturaRows([]);
            gdAlertify.notiError('Error al cargar facturas del periodo.');
        }
        setLoadingExistingFacturas(false);
    };

    const openAddFacturaModal = (row) => {
        setAddFacturaPaciente(row);
        setExistingFacturaRows([]);
        setAddFacturaRows([{
            nota: '',
            archivo: null,
            numero_factura: '',
            leyendoNumero: false
        }]);
        setShowAddFacturaModal(true);
        loadExistingFacturasForModal(row);
    };

    const updateExistingFacturaRow = (index, field, value) => {
        setExistingFacturaRows(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
    };

    const setExistingFacturaRowState = (index, patch) => {
        setExistingFacturaRows(prev => prev.map((item, idx) => idx === index ? { ...item, ...patch } : item));
    };

    const updateAddFacturaRow = (index, field, value) => {
        setAddFacturaRows(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
    };

    const addFacturaRow = () => {
        setAddFacturaRows(prev => [
            ...prev,
            { nota: '', archivo: null, numero_factura: '', leyendoNumero: false }
        ]);
    };

    const removeFacturaRow = (index) => {
        setAddFacturaRows(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleAddFacturaFile = async (index, file) => {
        setAddFacturaRows(prev => prev.map((item, idx) => idx === index ? {
            ...item,
            archivo: file,
            leyendoNumero: Boolean(file)
        } : item));

        if (!file) {
            return;
        }

        const formData = new FormData();
        formData.append('archivo', file);

        try {
            const response = await axiosInstance.post('/acuerdo-mutuo/cobranza/facturas/extraer-numero', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (!response.data.error && response.data.numero_factura) {
                updateAddFacturaRow(index, 'numero_factura', response.data.numero_factura);
            }
        } catch (error) {
            console.error('Error al extraer numero de factura:', error);
        } finally {
            updateAddFacturaRow(index, 'leyendoNumero', false);
        }
    };

    const guardarFacturas = async () => {
        if (!addFacturaPaciente) return;
        const queryFecha = isPage ? fechaFilter : fecha;
        if (!queryFecha) {
            gdAlertify.notiError('Seleccione un periodo antes de agregar facturas.');
            return;
        }

        setSavingFacturas(true);
        try {
            for (const factura of addFacturaRows) {
                if (!factura.archivo && !factura.nota) {
                    continue;
                }

                const formData = new FormData();
                formData.append('paciente_id', addFacturaPaciente.paciente_id);
                formData.append('periodo', queryFecha);
                formData.append('total_cobrar', 0);
                formData.append('total_abonado', 0);
                formData.append('nota', factura.nota || '');
                formData.append('numero_factura', factura.numero_factura || '');
                if (factura.archivo) {
                    formData.append('archivo', factura.archivo);
                }

                await axiosInstance.post('/acuerdo-mutuo/cobranza/facturas/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            gdAlertify.notiSuccess('Facturas registradas correctamente.');
            setShowAddFacturaModal(false);
            setAddFacturaPaciente(null);
            setAddFacturaRows([]);
            buscarFacturas();
        } catch (error) {
            console.error('Error al guardar facturas:', error);
            gdAlertify.notiError('Error al guardar las facturas.');
        }
        setSavingFacturas(false);
    };

    const guardarFacturaExistente = async (index) => {
        const factura = existingFacturaRows[index];
        if (!factura?.id) return;

        setExistingFacturaRowState(index, { saving: true });
        try {
            const resp = await axiosInstance.post('/acuerdo-mutuo/cobranza/facturas/actualizar', {
                factura_id: factura.id,
                numero_factura: factura.numero_factura || '',
                nota: factura.nota || ''
            });

            if (resp.data && !resp.data.error) {
                gdAlertify.notiSuccess('Factura actualizada.');
                buscarFacturas();
            } else {
                gdAlertify.notiError(resp.data.message || 'Error al actualizar factura.');
            }
        } catch (error) {
            console.error('Error al actualizar factura:', error);
            gdAlertify.notiError('Error al actualizar factura.');
        }
        setExistingFacturaRowState(index, { saving: false });
    };

    const eliminarFacturaExistente = async (index) => {
        const factura = existingFacturaRows[index];
        if (!factura?.id) return;

        gdAlertify.confirm({
            title: 'Eliminar Documento',
            message: `¿Desea eliminar el documento de factura ${factura.numero_factura ? `Nº ${factura.numero_factura}` : ''}?`,
            onConfirm: async () => {
                setExistingFacturaRowState(index, { deleting: true });
                try {
                    const resp = await axiosInstance.post('/acuerdo-mutuo/cobranza/facturas/delete', {
                        factura_id: factura.id
                    });

                    if (resp.data && !resp.data.error) {
                        gdAlertify.notiSuccess('Documento de factura eliminado.');
                        setExistingFacturaRows(prev => prev.filter((_, idx) => idx !== index));
                        buscarFacturas();
                    } else {
                        gdAlertify.notiError(resp.data.message || 'Error al eliminar el documento.');
                        setExistingFacturaRowState(index, { deleting: false });
                    }
                } catch (error) {
                    console.error('Error al eliminar documento:', error);
                    gdAlertify.notiError('Error al eliminar el documento de factura.');
                    setExistingFacturaRowState(index, { deleting: false });
                }
            },
            onCancel: () => {
                gdAlertify.notify('Eliminación cancelada', 'error');
            }
        });
    };

    const handleHistorial = async (row) => {
        setLoadingHistorial(true);
        try {
            const resp = await axiosInstance.get(`/acuerdo-mutuo/cobranza/facturas/historial/${row.paciente_id}`);
            if (!resp.data.error) {
                setVerHistorial({
                    id: row.paciente_id,
                    nombre: row.nombre_paciente,
                    facturas: resp.data.data
                });
            } else {
                gdAlertify.notiError(resp.data.message || "Error al cargar historial");
            }
        } catch (error) {
            console.error("Error historial:", error);
            gdAlertify.notiError("Error al conectar con el servidor para el historial");
        }
        setLoadingHistorial(false);
    };

    const generarPdfLigeroFacturas = (row, periodo) => {
        try {
            if (!window.jspdf?.jsPDF) return null;
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4');
            const mesPeriodo = formatearMesCorreo(periodo);
            const pacienteNombre = row.nombre_paciente || '';
            const totalCobrar = Number(row.total_cobrar || 0);
            const totalAbonado = Number(row.total_abonado || 0);
            const saldoFinal = Math.max(0, Number(row.saldo_pendiente ?? row.saldo_cartola ?? 0));

            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(`Detalle de Cobranza - ${pacienteNombre}`, 14, 15);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Periodo: ${mesPeriodo}`, 14, 22);
            doc.text(`Paciente: ${pacienteNombre}`, 14, 27);
            doc.setDrawColor(200);
            doc.line(14, 30, 196, 30);

            let y = 38;
            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Total a Cobrar:', 110, y);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalCobrar)}`, 155, y);

            if (totalAbonado > 0) {
                y += 6;
                doc.setFont(undefined, 'bold');
                doc.setTextColor(13, 110, 253);
                doc.text('Abono:', 110, y);
                doc.setFont(undefined, 'normal');
                doc.text(`-$${formatMoney(totalAbonado)}`, 155, y);
                doc.setTextColor(0);
            }

            y += 6;
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(saldoFinal > 0 ? 220 : 0, saldoFinal > 0 ? 0 : 128, 0);
            doc.text('Total Deuda:', 110, y);
            doc.text(`$${formatMoney(saldoFinal)}`, 155, y);

            return doc.output('datauristring');
        } catch (e) {
            console.error('Error generando PDF ligero de facturas:', e);
            return null;
        }
    };

    const handleAdjuntoExtraChange = (file) => {
        setAdjuntoExtra(file || null);
        setAdjuntoExtraBase64(null);
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => setAdjuntoExtraBase64(e.target.result);
        reader.readAsDataURL(file);
    };

    const openNotificarModal = async (row) => {
        const queryFecha = isPage ? fechaFilter : fecha;

        setNotificarRow(row);
        setEmailDestinatarios(emailNotificacionPaciente(row));
        setEmailAsunto(`Facturas SKMC - ${formatearMes(queryFecha)} - ${row.nombre_paciente}`);
        const facturaActual = row.archivo_factura ? [{
            id: row.factura_id || "actual",
            periodo: queryFecha,
            nota: row.numero_factura ? `Factura Nº ${row.numero_factura}` : "Factura actual",
            numero_factura: row.numero_factura || "",
            archivo_path: row.archivo_factura
        }] : [];
        setEmailMessage(crearMensajeFacturasDefault(queryFecha, facturaActual));
        setNotificarFacturas(facturaActual);
        setAdjuntoExtra(null);
        setAdjuntoExtraBase64(null);
        setPdfLigeroBase64(generarPdfLigeroFacturas(row, queryFecha));
        setAdjuntarPdfLigero(false);
        setShowNotificarModal(true);

        setLoadingNotificarFacturas(true);
        try {
            const resp = await axiosInstance.get(`/acuerdo-mutuo/cobranza/facturas/historial/${row.paciente_id}`);
            if (!resp.data.error) {
                const facturasPeriodo = (resp.data.data || []).filter((factura) =>
                    factura.periodo === queryFecha && factura.archivo_path
                );
                if (facturasPeriodo.length > 0 || !row.archivo_factura) {
                    setNotificarFacturas(facturasPeriodo);
                    setEmailMessage(crearMensajeFacturasDefault(queryFecha, facturasPeriodo));
                }
            }
        } catch (error) {
            console.error("Error al cargar facturas para notificacion:", error);
        } finally {
            setLoadingNotificarFacturas(false);
        }
    };

    const handleEnviarNotificacion = async () => {
        if (!notificarRow) return;
        if (!emailDestinatarios.trim()) {
            gdAlertify.notiError("Ingrese al menos un correo destinatario.");
            return;
        }

        const queryFecha = isPage ? fechaFilter : fecha;
        const numeroNotificacion = (Number(notificarRow.numero_notificacion_factura || notificarRow.numero_notificacion || 0) + 1).toString();


        setSendingNotificacion(true);
        try {
            const payload = {
                paciente_id: notificarRow.paciente_id,
                periodo: queryFecha,
                numero_notificacion: numeroNotificacion,
                destinatarios: emailDestinatarios,
                asunto: emailAsunto,
                mensaje: emailMessage + `<br/><br/>` + FIRMA_DEFAULT
            };

            if (adjuntarPdfLigero && pdfLigeroBase64) {
                payload.pdf_ligero_data = pdfLigeroBase64.split(',')[1];
                payload.pdf_ligero_name = `Detalle_Cobranza_${(notificarRow.nombre_paciente || '').replace(/\s+/g, '_')}_${queryFecha}.pdf`;
            }

            if (adjuntoExtraBase64 && adjuntoExtra) {
                payload.adjunto_extra_data = adjuntoExtraBase64.split(',')[1];
                payload.adjunto_extra_name = adjuntoExtra.name;
                payload.adjunto_extra_mime = adjuntoExtra.type || 'application/octet-stream';
            }

            const resp = await axiosInstance.post("/acuerdo-mutuo/cobranza/facturas/notificar", payload);

            if (!resp.data.error) {
                gdAlertify.notiSuccess(resp.data.message || "Notificación enviada correctamente.");
                setShowNotificarModal(false);
                buscarFacturas();
            } else {
                gdAlertify.notiError(resp.data.message || "Error al enviar la notificación.");
            }
        } catch (error) {
            console.error("Error al enviar notificación:", error);
            gdAlertify.notiError("Error al conectar con el servidor.");
        } finally {
            setSendingNotificacion(false);
        }
    };

    const columns = [
        { title: "RUT", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        { 
            title: "Total a Cobrar", 
            name: "total_cobrar", 
            type: "number",
            render: row => <span className="fw-bold fs-6">{`$${formatMoney(row.total_cobrar)}`}</span>
        },
        {
            title: "Email Notificación",
            name: "email_notificacion",
            render: row => <span className="small text-secondary">{emailNotificacionPaciente(row) || '—'}</span>
        },
        {
            title: "Saldo Anterior",
            name: "saldo_anterior",
            type: "number",
            render: row => {
                const anterior = Number(row.saldo_anterior ?? 0);
                const isAbono = anterior < 0;
                return (
                    <span className={`fw-bold fs-6 ${anterior > 0 ? "text-danger" : (isAbono ? "text-primary" : "text-success")}`}>
                        {isAbono ? `-$${formatMoney(Math.abs(anterior))} (A Favor)` : `$${formatMoney(anterior)}`}
                    </span>
                );
            }
        },
        {
            title: "Saldo Pendiente",
            name: "saldo_pendiente",
            type: "number",
            render: row => {
                const pendiente = Number(row.saldo_pendiente ?? row.saldo_cartola ?? 0);
                const isAbono = pendiente < 0;
                return (
                    <span className={`fw-bold fs-6 ${pendiente > 0 ? "text-danger" : (isAbono ? "text-primary" : "text-success")}`}>
                        {isAbono ? `-$${formatMoney(Math.abs(pendiente))} (A Favor)` : `$${formatMoney(pendiente)}`}
                    </span>
                );
            }
        },
        {
            title: "Estado Pago",
            name: "estado_pago_factura",
            render: row => (
                <span className={`badge ${row.estado_pago_factura === 'Pagado' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.estado_pago_factura}
                </span>
            )
        },
        {
            title: "Nº Factura",
            name: "numero_factura",
            render: row => row.numero_factura ? (
                <span className="badge bg-light text-dark border">{row.numero_factura}</span>
            ) : (
                <span className="text-muted">—</span>
            )
        },
        {
            title: "Notificado",
            name: "factura_notificada",
            render: row => {
                const notificado = Boolean(row.factura_notificada || Number(row.numero_notificacion_factura || 0) > 0);
                return (
                    <span className={`badge ${notificado ? 'bg-success' : 'bg-secondary'}`}>
                        {notificado ? 'Notificado' : 'No notificado'}
                    </span>
                );
            }
        },
        {
            title: "Apta Factura",
            name: "apta_para_facturar",
            render: row => {
                const pendiente = Number(row.saldo_pendiente ?? row.saldo_cartola ?? 0);
                const apta = pendiente <= 0;
                return (
                    <span className={`badge ${apta ? 'bg-success' : 'bg-secondary outline'}`} style={{ minWidth: '80px' }}>
                        {apta ? 'APTA' : 'CON DEUDA'}
                    </span>
                );
            }
        },
        {
            title: "Doc. Factura",
            name: "archivo_factura",
            render: row => {
                const count = row.facturas_count || 0;
                const hasFile = Boolean(row.archivo_factura);
                const label = count > 1 ? `${count} facturas` : hasFile ? '1 factura' : 'Sin factura';
                return (
                    <span className={`badge ${hasFile ? 'bg-info text-white' : 'bg-danger'}`}>
                        {hasFile ? (
                            <>
                                <i className="bi bi-check-circle-fill me-1"></i> {label}
                            </>
                        ) : (
                            <>
                                <i className="bi bi-x-circle-fill me-1"></i> {label}
                            </>
                        )}
                    </span>
                );
            }
        },
        {
            title: "Razón Social",
            name: "fac_razon_social",
            render: row => <span className="small">{row.fac_razon_social}</span>
        },
        {
            title: "RUT Fact.",
            name: "fac_rut",
            render: row => <span className="small">{row.fac_rut}</span>
        },
        {
            title: "Glosa",
            name: "fac_giro",
            render: row => <span className="small">{row.fac_giro}</span>
        },
        {
            title: "Email Fact.",
            name: "fac_email",
            render: row => <span className="small">{row.fac_email}</span>
        },
        {
            title: "Dirección",
            name: "fac_direccion",
            render: row => <span className="small">{row.fac_direccion}</span>
        },
        {
            title: "Comuna",
            name: "fac_comuna",
            render: row => <span className="small">{row.fac_comuna}</span>
        },
        {
            title: "Teléfono",
            name: "fac_telefono",
            render: row => <span className="small">{row.fac_telefono}</span>
        },
        {
            title: "Acciones",
            name: "acciones",
            render: row => (
                <div className="dropdown">
                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                       <i className="bi bi-gear"></i> Acciones
                    </button>
                    <ul className="dropdown-menu shadow">
                        <li>
                            <button className="dropdown-item" onClick={() => openAddFacturaModal(row)}>
                                <i className="bi bi-file-earmark-pdf text-success"></i> Facturas
                            </button>
                        </li>
                        <li>
                            <button className="dropdown-item" onClick={() => openNotificarModal(row)}>
                                <i className="bi bi-envelope text-primary"></i> Notificar Facturas
                            </button>
                        </li>
                        <li>
                            <button className="dropdown-item" onClick={() => {
                                setSelectedPacienteCartola(row);
                                setShowCartolaModal(true);
                            }}>
                                <i className="bi bi-journal-text text-warning"></i> Ver Cartola
                            </button>
                        </li>
                    </ul>
                </div>
            )
        }
    ];

    return (
        <div className="card mt-2 border-top-0 rounded-top-0">
            <div className="card-body">
                {isPage && (
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label">Mes</label>
                            <MonthInput
                                className="form-control"
                                value={fechaFilter}
                                onChange={e => setFechaFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary" onClick={buscarFacturas} disabled={!fechaFilter || loading}>
                                {loading ? "Buscando..." : "Buscar"}
                            </button>
                        </div>
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-muted">Facturas y Saldos {(isPage ? fechaFilter : fecha) ? `(${(isPage ? fechaFilter : fecha)})` : ""}</h5>
                    {!isPage && (
                        <button className="btn btn-sm btn-outline-primary" onClick={buscarFacturas} disabled={!fecha || loading}>
                             <i className="bi bi-arrow-clockwise"></i> Actualizar
                        </button>
                    )}
                </div>
                {(isPage ? fechaFilter : fecha) ? (
                    <GdTable
                        columns={columns}
                        data={resultados}
                        stickyHeader={true}
                        height={isPage ? 700 : 600}
                        headerHeight={50}
                        showCsvExport={true}
                        exportFilename={`facturas-y-saldos-${(isPage ? fechaFilter : fecha) || "mes"}.xlsx`}
                        loading={loading}
                    />
                ) : (
                    <div className="alert alert-info">
                        Seleccione un mes para consultar facturas.
                    </div>
                )}
            </div>

            {/* Modal de Historial */}
            {verHistorial && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-light">
                                <h5 className="modal-title">Historial de Facturas: <span className="text-primary">{verHistorial.nombre}</span></h5>
                                <button type="button" className="btn-close" onClick={() => setVerHistorial(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>Periodo</th>
                                                <th>Monto Cobrado</th>
                                                <th>Abonado</th>
                                                <th>Nº Factura</th>
                                                <th>Nota</th>
                                                <th>Notificado</th>
                                                <th>Estado</th>
                                                <th>Archivo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {verHistorial.facturas.length > 0 ? verHistorial.facturas.map((f, i) => (
                                                <tr key={i}>
                                                    <td>{f.periodo}</td>
                                                    <td>${formatMoney(f.total_cobrar)}</td>
                                                    <td>${formatMoney(f.total_abonado)}</td>
                                                    <td>{f.numero_factura || '—'}</td>
                                                    <td>{f.nota || '—'}</td>
                                                    <td>
                                                        <span className={`badge ${Number(f.notificado || f.numero_notificacion || 0) > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                            {Number(f.notificado || f.numero_notificacion || 0) > 0 ? 'Notificado' : 'No notificado'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${f.estado_pago === 'Pagado' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                            {f.estado_pago}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {f.archivo_path && (
                                                            <a href={f.archivo_path} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info text-white">
                                                                <i className="bi bi-eye"></i> Ver
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="8" className="text-center py-3">No hay facturas registradas anteriormente.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setVerHistorial(null)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {loadingHistorial && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999 }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            )}

            {showAddFacturaModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-light">
                                <h5 className="modal-title">Facturas - <span className="text-primary">{addFacturaPaciente?.nombre_paciente}</span></h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddFacturaModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <h6 className="mb-0">Facturas del periodo</h6>
                                        {loadingExistingFacturas && <span className="spinner-border spinner-border-sm text-primary"></span>}
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '20%' }}>Nº Factura</th>
                                                    <th style={{ width: '35%' }}>Nota</th>
                                                    <th style={{ width: '25%' }}>Archivo</th>
                                                    <th style={{ width: '20%' }} className="text-end">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {existingFacturaRows.length > 0 ? existingFacturaRows.map((factura, index) => (
                                                    <tr key={factura.id}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={factura.numero_factura || ''}
                                                                onChange={e => updateExistingFacturaRow(index, 'numero_factura', e.target.value)}
                                                                placeholder="Ej: 12525"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={factura.nota || ''}
                                                                onChange={e => updateExistingFacturaRow(index, 'nota', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <a href={factura.archivo_path} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                                                                <i className="bi bi-eye me-1"></i> Ver PDF
                                                            </a>
                                                        </td>
                                                        <td className="text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => guardarFacturaExistente(index)}
                                                                disabled={factura.saving || factura.deleting}
                                                            >
                                                                {factura.saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-save"></i>}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => eliminarFacturaExistente(index)}
                                                                disabled={factura.saving || factura.deleting}
                                                            >
                                                                {factura.deleting ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center text-muted py-3">
                                                            {loadingExistingFacturas ? 'Cargando facturas...' : 'No hay facturas subidas para este periodo.'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <h6 className="mb-2">Nueva factura</h6>
                                <div className="table-responsive">
                                    <table className="table table-borderless align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '20%' }}>Nº Factura</th>
                                                <th style={{ width: '35%' }}>Nota</th>
                                                <th style={{ width: '35%' }}>Archivo (opcional)</th>
                                                <th style={{ width: '10%' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {addFacturaRows.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="input-group">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={item.numero_factura || ''}
                                                                onChange={e => updateAddFacturaRow(index, 'numero_factura', e.target.value)}
                                                                placeholder="Ej: 12525"
                                                            />
                                                            {item.leyendoNumero && (
                                                                <span className="input-group-text">
                                                                    <span className="spinner-border spinner-border-sm"></span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={item.nota}
                                                            onChange={e => updateAddFacturaRow(index, 'nota', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            accept=".pdf,.png,.jpg,.jpeg"
                                                            onChange={e => handleAddFacturaFile(index, e.target.files?.[0] ?? null)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFacturaRow(index)}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-3">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addFacturaRow}>
                                        <i className="bi bi-plus-circle me-1"></i> Agregar otra factura
                                    </button>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddFacturaModal(false)}>Cerrar</button>
                                <button type="button" className="btn btn-primary" onClick={guardarFacturas} disabled={savingFacturas}>
                                    {savingFacturas ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</>
                                    ) : 'Guardar facturas'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <CartolaPacienteModal
                show={showCartolaModal}
                handleClose={() => setShowCartolaModal(false)}
                paciente={selectedPacienteCartola}
            />

            {/* Modal de Notificación de Facturas */}
            {showNotificarModal && notificarRow && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-envelope me-2"></i>Notificar Facturas
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowNotificarModal(false)} disabled={sendingNotificacion}></button>
                            </div>
                            <div
                                className="modal-body"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
                                    gap: '1rem'
                                }}
                            >
                                {/* Tutor de Pago — span completo, arriba de todo */}
                                <div className="p-3 bg-primary bg-opacity-10 border border-primary rounded mb-1" style={{ gridColumn: '1 / -1' }}>
                                    <div className="fw-bold text-primary small text-uppercase mb-2">
                                        <i className="bi bi-person-check me-1"></i> Tutor de Pago
                                    </div>
                                    <div className="row g-2 align-items-center">
                                        <div className="col-auto">
                                            <span className="fw-semibold">{notificarRow.tutor_nombre || '—'}</span>
                                        </div>
                                        <div className="col">
                                            <span className="text-muted small">
                                                {emailNotificacionPaciente(notificarRow) || 'Sin correo registrado'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3" style={{ gridColumn: '1' }}>
                                    <label className="form-label fw-bold">Paciente</label>
                                    <div className="form-control-plaintext fs-6">{notificarRow.nombre_paciente}</div>
                                </div>

                                <div className="mb-3" style={{ gridColumn: '1' }}>
                                    <label className="form-label fw-bold">Correos Destinatarios *</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={emailDestinatarios}
                                        onChange={e => setEmailDestinatarios(e.target.value)}
                                        placeholder="ejemplo@mail.com, otro@mail.com"
                                        disabled={sendingNotificacion}
                                    />
                                    <small className="form-text text-muted">Separar múltiples correos con comas</small>
                                </div>

                                <div className="mb-3" style={{ gridColumn: '1' }}>
                                    <label className="form-label fw-bold">Asunto</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={emailAsunto}
                                        onChange={e => setEmailAsunto(e.target.value)}
                                        disabled={sendingNotificacion}
                                    />
                                </div>

                                <div className="mb-3" style={{ gridColumn: '1' }}>
                                    <label className="form-label fw-bold">
                                        Adjunto adicional <span className="text-muted fw-normal">(opcional)</span>
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                                        disabled={sendingNotificacion}
                                        onChange={e => handleAdjuntoExtraChange(e.target.files?.[0] ?? null)}
                                    />
                                    {adjuntoExtra && (
                                        <div className="mt-1 small text-success">
                                            <i className="bi bi-check-circle me-1"></i>{adjuntoExtra.name}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3" style={{ gridColumn: '2', gridRow: '2 / span 4' }}>
                                    <label className="form-label fw-bold">Mensaje</label>
                                    <div style={{ backgroundColor: '#fff', borderRadius: '4px' }}>
                                        <ReactQuill
                                            theme="snow"
                                            value={emailMessage}
                                            onChange={setEmailMessage}
                                            readOnly={sendingNotificacion}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ list: 'ordered' }, { list: 'bullet' }],
                                                    [{ color: [] }, { background: [] }],
                                                ]
                                            }}
                                            style={{ height: '255px', marginBottom: '50px' }}
                                        />
                                    </div>
                                </div>

                                <div className="mt-2 p-3 bg-white border rounded shadow-sm" style={{ opacity: 0.9, gridColumn: '1' }}>
                                    <div className="small text-muted mb-2">
                                        <i className="bi bi-info-circle me-1"></i> Firma automatica
                                    </div>
                                    <div dangerouslySetInnerHTML={{ __html: FIRMA_DEFAULT }} />
                                </div>

                                <div className="border rounded bg-light p-3 mt-3" style={{ gridColumn: '1 / -1' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0 fw-bold">
                                            <i className="bi bi-paperclip me-2"></i>Archivos adjuntos
                                        </h6>
                                        {loadingNotificarFacturas && (
                                            <span className="small text-muted">
                                                <span className="spinner-border spinner-border-sm me-1"></span>Cargando...
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                        <label
                                            className="btn btn-sm btn-outline-secondary bg-white text-start d-flex align-items-center gap-1 mb-0"
                                            style={{ cursor: pdfLigeroBase64 ? 'pointer' : 'not-allowed' }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="form-check-input mt-0"
                                                checked={adjuntarPdfLigero}
                                                disabled={!pdfLigeroBase64}
                                                onChange={(e) => setAdjuntarPdfLigero(e.target.checked)}
                                            />
                                            <i className="bi bi-file-earmark-pdf me-1 text-danger"></i>
                                            PDF Detalle Cobranza
                                            {!pdfLigeroBase64 && (
                                                <span className="badge bg-warning text-dark ms-2 small">Sin jsPDF</span>
                                            )}
                                        </label>
                                        {notificarFacturas.length > 0 ? notificarFacturas.map((factura, index) => (
                                            <a
                                                key={factura.id || `${factura.archivo_path}-${index}`}
                                                href={factura.archivo_path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-info bg-white text-start"
                                            >
                                                <i className="bi bi-file-earmark-pdf me-1"></i>
                                                {factura.numero_factura ? `Factura Nº ${factura.numero_factura}` : (factura.nota || `Factura ${index + 1}`)}
                                            </a>
                                        )) : (
                                            <span className="text-muted small">Sin facturas subidas para este periodo.</span>
                                        )}
                                        {adjuntoExtra && (
                                            <span className="btn btn-sm btn-outline-success bg-white text-start" style={{ cursor: 'default' }}>
                                                <i className="bi bi-paperclip me-1"></i>{adjuntoExtra.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowNotificarModal(false)} disabled={sendingNotificacion}>
                                    Cancelar
                                </button>
                                <button type="button" className="btn btn-info text-white" onClick={handleEnviarNotificacion} disabled={sendingNotificacion}>
                                    {sendingNotificacion ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                                    ) : (
                                        <><i className="bi bi-send me-1"></i>Enviar Notificación</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CobranzaFacturas;
